import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { projectsAPI, membersAPI, unitsAPI, formatApiErrorDetail } from '../../lib/api';
import { Plus, MagnifyingGlass, Eye, PencilSimple, Briefcase } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const STATUSES = ['planning', 'in_progress', 'on_hold', 'completed', 'cancelled'];
const PRIORITIES = ['low', 'medium', 'high', 'critical'];
const CATEGORIES = ['charitable', 'administrative', 'ceremonial', 'communications', 'education', 'other'];

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [viewingProject, setViewingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '', category: 'other', description: '', status: 'planning',
    priority: 'medium', lead_officer_id: '', owning_unit_id: '',
    start_date: '', target_date: '', internal_notes: '', approval_status: 'pending'
  });

  const fetchData = async () => {
    try {
      const [projectsRes, membersRes, unitsRes] = await Promise.all([
        projectsAPI.getAll(),
        membersAPI.getAll(),
        unitsAPI.getAll()
      ]);
      setProjects(projectsRes.data);
      setMembers(membersRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await projectsAPI.update(editingProject.id, formData);
        toast.success('Project updated successfully');
      } else {
        await projectsAPI.create(formData);
        toast.success('Project created successfully');
      }
      setShowModal(false);
      setEditingProject(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title || '',
      category: project.category || 'other',
      description: project.description || '',
      status: project.status || 'planning',
      priority: project.priority || 'medium',
      lead_officer_id: project.lead_officer_id || '',
      owning_unit_id: project.owning_unit_id || '',
      start_date: project.start_date || '',
      target_date: project.target_date || '',
      internal_notes: project.internal_notes || '',
      approval_status: project.approval_status || 'pending'
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '', category: 'other', description: '', status: 'planning',
      priority: 'medium', lead_officer_id: '', owning_unit_id: '',
      start_date: '', target_date: '', internal_notes: '', approval_status: 'pending'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-green-100 text-green-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'on_hold': return 'bg-yellow-100 text-yellow-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="projects-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-[#1A1614]">Projects</h1>
            <p className="text-[#8A847A]">{projects.length} total projects</p>
          </div>
          <Button 
            onClick={() => { resetForm(); setEditingProject(null); setShowModal(true); }}
            className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none"
            data-testid="add-project-button"
          >
            <Plus size={18} className="mr-2" />
            New Project
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 bg-white border border-[#D8D2C4] p-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A847A]" />
            <Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-none border-[#D8D2C4]"
              data-testid="project-search-input"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 rounded-none" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
            {filteredProjects.map(project => (
              <div 
                key={project.id}
                className="bg-white border border-[#D8D2C4] p-6 hover:border-[#C5A059] transition-colors"
                data-testid={`project-card-${project.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#F5F2EB] flex items-center justify-center text-[#7B1818]">
                      <Briefcase size={20} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-[#1A1614] line-clamp-1">{project.title}</h3>
                      <p className="text-xs text-[#8A847A] capitalize">{project.category}</p>
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-[#4A453E] line-clamp-2 mb-4">{project.description || 'No description'}</p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`px-2 py-1 text-xs capitalize ${getStatusColor(project.status)}`}>
                    {project.status?.replace('_', ' ')}
                  </span>
                  <span className={`px-2 py-1 text-xs capitalize ${getPriorityColor(project.priority)}`}>
                    {project.priority}
                  </span>
                </div>

                {project.target_date && (
                  <p className="text-xs text-[#8A847A] mb-4">
                    Target: {new Date(project.target_date).toLocaleDateString()}
                  </p>
                )}

                <div className="flex justify-end gap-2 pt-4 border-t border-[#E8E4D9]">
                  <button 
                    onClick={() => setViewingProject(project)}
                    className="p-2 hover:bg-[#F5F2EB] text-[#4A453E]"
                    data-testid={`view-project-${project.id}`}
                  >
                    <Eye size={18} />
                  </button>
                  <button 
                    onClick={() => handleEdit(project)}
                    className="p-2 hover:bg-[#F5F2EB] text-[#4A453E]"
                    data-testid={`edit-project-${project.id}`}
                  >
                    <PencilSimple size={18} />
                  </button>
                </div>
              </div>
            ))}
            {filteredProjects.length === 0 && (
              <div className="col-span-full text-center py-12 text-[#8A847A]">
                No projects found
              </div>
            )}
          </div>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {editingProject ? 'Edit Project' : 'New Project'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="project-form">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  className="rounded-none"
                  data-testid="project-input-title"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger className="rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Priority</label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                    <SelectTrigger className="rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger className="rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Lead Officer</label>
                  <Select value={formData.lead_officer_id} onValueChange={(v) => setFormData({...formData, lead_officer_id: v})}>
                    <SelectTrigger className="rounded-none">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">None</SelectItem>
                      {members.map(m => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Date</label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Target Date</label>
                  <Input
                    type="date"
                    value={formData.target_date}
                    onChange={(e) => setFormData({...formData, target_date: e.target.value})}
                    className="rounded-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Owning Unit</label>
                <Select value={formData.owning_unit_id} onValueChange={(v) => setFormData({...formData, owning_unit_id: v})}>
                  <SelectTrigger className="rounded-none">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">None</SelectItem>
                    {units.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.type})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="rounded-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Internal Notes</label>
                <Textarea
                  value={formData.internal_notes}
                  onChange={(e) => setFormData({...formData, internal_notes: e.target.value})}
                  rows={2}
                  className="rounded-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-none">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none" data-testid="project-submit-button">
                  {editingProject ? 'Update Project' : 'Create Project'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Modal */}
        <Dialog open={!!viewingProject} onOpenChange={() => setViewingProject(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Project Details</DialogTitle>
            </DialogHeader>
            {viewingProject && (
              <div className="space-y-4" data-testid="project-details">
                <h3 className="font-heading text-xl text-[#1A1614]">{viewingProject.title}</h3>
                <div className="flex gap-2">
                  <span className={`px-2 py-1 text-xs capitalize ${getStatusColor(viewingProject.status)}`}>
                    {viewingProject.status?.replace('_', ' ')}
                  </span>
                  <span className={`px-2 py-1 text-xs capitalize ${getPriorityColor(viewingProject.priority)}`}>
                    {viewingProject.priority}
                  </span>
                </div>
                <p className="text-[#4A453E]">{viewingProject.description || 'No description'}</p>
                <div className="grid grid-cols-2 gap-4 text-sm pt-4 border-t border-[#D8D2C4]">
                  <div><span className="text-[#8A847A]">Category:</span> <span className="capitalize">{viewingProject.category}</span></div>
                  <div><span className="text-[#8A847A]">Start:</span> {viewingProject.start_date || '-'}</div>
                  <div><span className="text-[#8A847A]">Target:</span> {viewingProject.target_date || '-'}</div>
                  <div><span className="text-[#8A847A]">Approval:</span> <span className="capitalize">{viewingProject.approval_status}</span></div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ProjectsPage;
